import { atom, atomFamily } from 'recoil';
import type { ImageStreamProgress } from '~/services/imageStreaming/imageStreamProgress';

/**
 * Per-series streaming completeness, keyed by Series Instance UID.
 *
 * The Recoil half of the bridge described in
 * `~/services/imageStreaming/imageStreamProgress`: `Image3D` is mutated by the
 * stream controller outside React, so this family — not the domain object — is
 * what components subscribe to. It is written only by `useSliceStreaming`, and
 * only through a throttle.
 *
 * A family rather than one map-valued atom on purpose: a slice landing in the
 * secondary series must not re-render every control gated on the primary one.
 *
 * `undefined` means "nothing has been recorded for this series". Consumers must
 * NOT read that as "incomplete": it is also the state of every locally opened
 * file and every fully parsed series, none of which stream.
 * `useImageStreamProgress` resolves it against the live domain object instead,
 * which also closes the window between the viewer opening and the first
 * progress write.
 */
export const imageStreamProgressFamily = atomFamily<
    ImageStreamProgress | undefined,
    string
>({
    key: 'imageStreamProgressBySeries',
    default: undefined,
});

/**
 * Series Instance UIDs of every image in the session that is not the whole
 * volume — still streaming, or finished short and permanently incomplete.
 *
 * The family above answers "how is THIS image doing", which needs one
 * subscription per image and therefore only works where the set of images is
 * fixed and known at render time. This atom answers "is anything in this
 * arbitrary set of images incomplete", which is what the export selection
 * needs: the user can tick any number of images, including ones that are
 * neither the primary nor the secondary and so are subscribed nowhere.
 *
 * It is deliberately a set of ids and not a set of progress values: it changes
 * only when a series completes, not on every slice, so subscribers re-render
 * a handful of times per load rather than once per throttle tick.
 *
 * An incomplete-final series stays listed. It never becomes complete, and the
 * operations gated on this list stay gated for it.
 */
export const incompleteSeriesUidsState = atom<readonly string[]>({
    key: 'incompleteSeriesUids',
    default: [],
});
